import { useState, useEffect } from "react";
import { supabase } from "../supabase";

const ADMIN_PASSWORD = "grassroots2026"; // Change this to your own password

export default function AdminPanel() {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");

  useEffect(() => {
    if (authed) fetchListings();
  }, [authed]);

  const fetchListings = async () => {
    setLoading(true);
    const { data: pendingData } = await supabase
      .from("listings")
      .select("*")
      .eq("status", "pending")
      .order("created_at", { ascending: false });

    const { data: approvedData } = await supabase
      .from("listings")
      .select("*")
      .eq("status", "approved")
      .order("created_at", { ascending: false });

    setPending(pendingData || []);
    setApproved(approvedData || []);
    setLoading(false);
  };

  const updateStatus = async (id, status) => {
    await supabase.from("listings").update({ status }).eq("id", id);

    // When approving — find matching parents and alert them
    if (status === "approved") {
      const listing = [...pending, ...approved].find(l => l.id === id);
      if (listing) {
        // Find parents who registered for same age group AND similar location
        const { data: matches } = await supabase
          .from("notifications")
          .select("*")
          .eq("age_group", listing.age_group);

        if (matches && matches.length > 0) {
          // Filter by search radius preference
          const locationMatches = matches.filter(n => {
            const radius = n.search_radius || "town";
            const nTown = (n.town || "").toLowerCase().trim();
            const lTown = (listing.town || "").toLowerCase().trim();
            const lRegion = (listing.region || "").toLowerCase().trim();
            if (radius === "anywhere") return true;
            if (radius === "county") return nTown === lTown || nTown === lRegion;
            return nTown === lTown; // "town" (default)
          });

          if (locationMatches.length > 0) {
            const recipients = locationMatches.map(n => ({
              email: n.email,
              parent_name: n.name,
            }));

            await supabase.functions.invoke("send-email", {
              body: {
                type: "alert",
                data: {
                  club: listing.club,
                  age_group: listing.age_group,
                  town: listing.town,
                  region: listing.region,
                  day: listing.day,
                  level: listing.level,
                  positions: listing.positions?.join(", "),
                },
                recipients,
              },
            });

            alert(`✅ Listing approved! ${locationMatches.length} parent${locationMatches.length !== 1 ? "s" : ""} notified by email.`);
          } else {
            alert("✅ Listing approved! No matching parents to notify yet.");
          }
        } else {
          alert("✅ Listing approved! No matching parents to notify yet.");
        }
      }
    }

    fetchListings();
  };

  const deleteListing = async (id) => {
    if (!window.confirm("Permanently delete this listing?")) return;
    await supabase.from("listings").delete().eq("id", id);
    fetchListings();
  };

  const inputStyle = {
    width: "100%", background: "#1f2937", border: "1px solid #374151",
    borderRadius: 8, padding: "12px 14px", color: "#f3f4f6", fontSize: 14,
    outline: "none", boxSizing: "border-box", fontFamily: "inherit"
  };

  // ── Login Screen ──
  if (!authed) return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <div style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: 16, padding: 32, maxWidth: 360, width: "100%", textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🔐</div>
        <div style={{ color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Admin Access</div>
        <input
          type="password"
          style={inputStyle}
          placeholder="Enter password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && password === ADMIN_PASSWORD) setAuthed(true); }}
        />
        <button
          onClick={() => {
            if (password === ADMIN_PASSWORD) setAuthed(true);
            else alert("Incorrect password");
          }}
          style={{ width: "100%", marginTop: 12, background: "#10b981", color: "#fff", border: "none", borderRadius: 10, padding: "12px 0", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 14, cursor: "pointer", letterSpacing: 1 }}
        >
          LOGIN
        </button>
      </div>
    </div>
  );

  // ── Admin Dashboard ──
  const list = tab === "pending" ? pending : approved;

  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ color: "#10b981", fontFamily: "'Oswald', sans-serif", fontSize: 11, letterSpacing: 3, marginBottom: 6 }}>GRASSROOTING ADMIN</div>
        <h2 style={{ color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 24, fontWeight: 700, margin: 0 }}>Listing Approvals</h2>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <button onClick={() => setTab("pending")} style={{
          background: tab === "pending" ? "#f59e0b" : "#1f2937", color: tab === "pending" ? "#000" : "#9ca3af",
          border: "none", borderRadius: 20, padding: "8px 18px", fontSize: 13, fontWeight: 700,
          fontFamily: "'Oswald', sans-serif", cursor: "pointer", letterSpacing: 0.5
        }}>
          PENDING ({pending.length})
        </button>
        <button onClick={() => setTab("approved")} style={{
          background: tab === "approved" ? "#10b981" : "#1f2937", color: tab === "approved" ? "#fff" : "#9ca3af",
          border: "none", borderRadius: 20, padding: "8px 18px", fontSize: 13, fontWeight: 700,
          fontFamily: "'Oswald', sans-serif", cursor: "pointer", letterSpacing: 0.5
        }}>
          LIVE ({approved.length})
        </button>
        <button onClick={fetchListings} style={{
          background: "#1f2937", color: "#9ca3af", border: "none", borderRadius: 20, padding: "8px 14px",
          fontSize: 13, fontWeight: 700, fontFamily: "'Oswald', sans-serif", cursor: "pointer"
        }}>
          🔄 REFRESH
        </button>
      </div>

      {loading && <div style={{ color: "#6b7280", fontSize: 13 }}>Loading...</div>}

      {!loading && list.length === 0 && (
        <div style={{ textAlign: "center", color: "#4b5563", padding: 40, fontSize: 14, background: "#111827", borderRadius: 14, border: "1px solid #1f2937" }}>
          {tab === "pending" ? "No pending listings — all caught up! 🎉" : "No live listings yet."}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {list.map(item => (
          <div key={item.id} style={{ background: "#111827", border: "1px solid #1f2937", borderRadius: 14, padding: "18px 20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontSize: 18, fontWeight: 700 }}>{item.club}</div>
                <div style={{ color: "#6b7280", fontSize: 12, marginTop: 2 }}>{item.town}, {item.region} · {item.league || "No league specified"}</div>
              </div>
              <div style={{ background: "#1f2937", color: "#f9fafb", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 16, borderRadius: 8, padding: "4px 12px" }}>
                {item.age_group}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 8, marginBottom: 10 }}>
              <div style={{ background: "#1f2937", borderRadius: 8, padding: "8px 12px" }}>
                <div style={{ color: "#4b5563", fontSize: 9, marginBottom: 2 }}>DAY</div>
                <div style={{ color: "#f3f4f6", fontSize: 13, fontWeight: 600 }}>{item.day || "—"}</div>
              </div>
              <div style={{ background: "#1f2937", borderRadius: 8, padding: "8px 12px" }}>
                <div style={{ color: "#4b5563", fontSize: 9, marginBottom: 2 }}>DIVISION</div>
                <div style={{ color: "#f3f4f6", fontSize: 13, fontWeight: 600 }}>{item.division || "—"}</div>
              </div>
              <div style={{ background: "#1f2937", borderRadius: 8, padding: "8px 12px" }}>
                <div style={{ color: "#4b5563", fontSize: 9, marginBottom: 2 }}>LEVEL</div>
                <div style={{ color: "#f3f4f6", fontSize: 13, fontWeight: 600 }}>{item.level || "—"}</div>
              </div>
            </div>

            {item.level_desc && (
              <div style={{ color: "#9ca3af", fontSize: 13, lineHeight: 1.6, marginBottom: 10, background: "#0d1117", borderRadius: 8, padding: "10px 12px" }}>
                {item.level_desc}
              </div>
            )}

            {item.positions && item.positions.length > 0 && (
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                {item.positions.map((pos, i) => (
                  <span key={i} style={{ background: "#10b98122", color: "#10b981", borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>{pos}</span>
                ))}
              </div>
            )}

            <div style={{ color: "#6b7280", fontSize: 12, marginBottom: 14 }}>
              ✉️ {item.contact_email} {item.phone && `· 📞 ${item.phone}`}
              {item.fa_number && ` · FA: ${item.fa_number}`}
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {tab === "pending" ? (
                <>
                  <button onClick={() => updateStatus(item.id, "approved")} style={{ background: "#10b981", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", letterSpacing: 0.5 }}>
                    ✅ APPROVE & PUBLISH
                  </button>
                  <button onClick={() => deleteListing(item.id)} style={{ background: "#1f2937", color: "#ef4444", border: "1px solid #ef444433", borderRadius: 8, padding: "10px 20px", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", letterSpacing: 0.5 }}>
                    ✕ REJECT
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => updateStatus(item.id, "filled")} style={{ background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", letterSpacing: 0.5 }}>
                    🎉 MARK AS FILLED
                  </button>
                  <button onClick={() => deleteListing(item.id)} style={{ background: "#1f2937", color: "#ef4444", border: "1px solid #ef444433", borderRadius: 8, padding: "10px 20px", fontFamily: "'Oswald', sans-serif", fontWeight: 700, fontSize: 13, cursor: "pointer", letterSpacing: 0.5 }}>
                    🗑️ REMOVE
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
